/**
 * Hooks for artefact template management
 *
 * Provides hooks for listing, creating, updating, deleting artefact templates
 * at both organisation and case levels, including resolved default resolution.
 */

import { useState, useEffect, useCallback } from 'react'
import type {
  ArtefactTemplate,
  CollectorSettings,
  CreateArtefactTemplateInput,
  ResolvedDefaultTemplate,
  TemplateActiveUsage,
  TestBuildInput,
  TestBuildResult,
  UpdateArtefactTemplateInput,
  UpsertCollectorSettingsInput,
} from '../types/artefact-templates'
import {
  listOrgTemplates,
  createOrgTemplate,
  updateOrgTemplate,
  deleteOrgTemplate,
  listCaseTemplates,
  createCaseTemplate,
  updateCaseTemplate,
  deleteCaseTemplate,
  getResolvedDefault,
  getCollectorSettings,
  upsertCollectorSettings,
  deleteCollectorSettings,
  getTemplateActiveUsage,
  recordTestBuild,
} from '../services/artefact-templates'
import { runMutation } from './use-collection-requests/run-mutation'

// =============================================================================
// useArtefactTemplates - List templates by scope
// =============================================================================

interface UseArtefactTemplatesResult {
  templates: ArtefactTemplate[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch artefact templates for a case (full hierarchy)
 */
export function useArtefactTemplates(caseId: string | null): UseArtefactTemplatesResult {
  const [templates, setTemplates] = useState<ArtefactTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setTemplates([])
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await listCaseTemplates(caseId)
      setTemplates(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch artefact templates')
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { templates, loading, error, refetch: fetch }
}

/**
 * Hook to fetch organisation-level artefact templates
 */
export function useOrgArtefactTemplates(): UseArtefactTemplatesResult {
  const [templates, setTemplates] = useState<ArtefactTemplate[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const data = await listOrgTemplates()
      setTemplates(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch organisation templates')
      setTemplates([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { templates, loading, error, refetch: fetch }
}

// =============================================================================
// useResolvedDefaultTemplate - Get resolved default for a case
// =============================================================================

interface UseResolvedDefaultResult {
  resolved: ResolvedDefaultTemplate | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

/**
 * Hook to fetch the resolved default artefact template for a case
 */
export function useResolvedDefaultTemplate(caseId: string | null): UseResolvedDefaultResult {
  const [resolved, setResolved] = useState<ResolvedDefaultTemplate | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!caseId) {
      setResolved(null)
      setError(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const data = await getResolvedDefault(caseId)
      setResolved(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch resolved default template')
      setResolved(null)
    } finally {
      setLoading(false)
    }
  }, [caseId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { resolved, loading, error, refetch: fetch }
}

// =============================================================================
// useArtefactTemplateMutations - Create/update/delete operations
// =============================================================================

interface UseArtefactTemplateMutationsResult {
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  createOrg: (input: CreateArtefactTemplateInput) => Promise<ArtefactTemplate | null>
  updateOrg: (
    templateId: string,
    input: UpdateArtefactTemplateInput
  ) => Promise<ArtefactTemplate | null>
  deleteOrg: (templateId: string) => Promise<boolean>
  createCase: (
    caseId: string,
    input: CreateArtefactTemplateInput
  ) => Promise<ArtefactTemplate | null>
  updateCase: (
    caseId: string,
    templateId: string,
    input: UpdateArtefactTemplateInput
  ) => Promise<ArtefactTemplate | null>
  deleteCase: (caseId: string, templateId: string) => Promise<boolean>
  clearError: () => void
}

/**
 * Hook for artefact template mutations (org and case level)
 */
export function useArtefactTemplateMutations(): UseArtefactTemplateMutationsResult {
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const createOrg = useCallback(
    (input: CreateArtefactTemplateInput) => runMutation(
      { setLoading: setCreating, setError, errorMessage: 'Failed to create organisation template', fallback: null },
      () => createOrgTemplate(input)
    ), []
  )

  const updateOrgFn = useCallback(
    (templateId: string, input: UpdateArtefactTemplateInput) => runMutation(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to update organisation template', fallback: null },
      () => updateOrgTemplate(templateId, input)
    ), []
  )

  const deleteOrgFn = useCallback(
    (templateId: string) => runMutation(
      { setLoading: setDeleting, setError, errorMessage: 'Failed to delete organisation template', fallback: false },
      async () => { await deleteOrgTemplate(templateId); return true }
    ), []
  )

  const createCaseFn = useCallback(
    (caseId: string, input: CreateArtefactTemplateInput) => runMutation(
      { setLoading: setCreating, setError, errorMessage: 'Failed to create case template', fallback: null },
      () => createCaseTemplate(caseId, input)
    ), []
  )

  const updateCaseFn = useCallback(
    (caseId: string, templateId: string, input: UpdateArtefactTemplateInput) => runMutation(
      { setLoading: setUpdating, setError, errorMessage: 'Failed to update case template', fallback: null },
      () => updateCaseTemplate(caseId, templateId, input)
    ), []
  )

  const deleteCaseFn = useCallback(
    (caseId: string, templateId: string) => runMutation(
      { setLoading: setDeleting, setError, errorMessage: 'Failed to delete case template', fallback: false },
      async () => { await deleteCaseTemplate(caseId, templateId); return true }
    ), []
  )

  const clearError = useCallback(() => { setError(null) }, [])

  return {
    creating, updating, deleting, error,
    createOrg, updateOrg: updateOrgFn, deleteOrg: deleteOrgFn,
    createCase: createCaseFn, updateCase: updateCaseFn, deleteCase: deleteCaseFn,
    clearError,
  }
}

// =============================================================================
// useCollectorSettings - Get/upsert/delete collector settings
// =============================================================================

interface UseCollectorSettingsResult {
  settings: CollectorSettings | null
  loading: boolean
  saving: boolean
  error: string | null
  refetch: () => Promise<void>
  save: (input: UpsertCollectorSettingsInput) => Promise<CollectorSettings | null>
  remove: () => Promise<boolean>
}

export function useCollectorSettings(templateId: string | null): UseCollectorSettingsResult {
  const [settings, setSettings] = useState<CollectorSettings | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!templateId) {
      setSettings(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await getCollectorSettings(templateId)
      setSettings(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch collector settings')
    } finally {
      setLoading(false)
    }
  }, [templateId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const save = useCallback(
    async (input: UpsertCollectorSettingsInput): Promise<CollectorSettings | null> => {
      if (!templateId) return null
      setSaving(true)
      setError(null)
      try {
        const data = await upsertCollectorSettings(templateId, input)
        setSettings(data)
        return data
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save collector settings')
        return null
      } finally {
        setSaving(false)
      }
    },
    [templateId]
  )

  const remove = useCallback(async (): Promise<boolean> => {
    if (!templateId) return false
    setSaving(true)
    setError(null)
    try {
      await deleteCollectorSettings(templateId)
      setSettings(null)
      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete collector settings')
      return false
    } finally {
      setSaving(false)
    }
  }, [templateId])

  return { settings, loading, saving, error, refetch: fetch, save, remove }
}

// =============================================================================
// useTemplateActiveUsage - Fetch active usage for deployment warning
// =============================================================================

interface UseTemplateActiveUsageResult {
  usage: TemplateActiveUsage | null
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useTemplateActiveUsage(templateId: string | null): UseTemplateActiveUsageResult {
  const [usage, setUsage] = useState<TemplateActiveUsage | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetch = useCallback(async () => {
    if (!templateId) {
      setUsage(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await getTemplateActiveUsage(templateId)
      setUsage(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch active usage')
    } finally {
      setLoading(false)
    }
  }, [templateId])

  useEffect(() => {
    void fetch()
  }, [fetch])

  return { usage, loading, error, refetch: fetch }
}

// =============================================================================
// useTemplateTestBuild - Trigger test build mutation
// =============================================================================

interface UseTemplateTestBuildResult {
  building: boolean
  error: string | null
  triggerBuild: (templateId: string, input: TestBuildInput) => Promise<TestBuildResult | null>
}

export function useTemplateTestBuild(): UseTemplateTestBuildResult {
  const [building, setBuilding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const triggerBuild = useCallback(
    async (templateId: string, input: TestBuildInput): Promise<TestBuildResult | null> => {
      setBuilding(true)
      setError(null)
      try {
        return await recordTestBuild(templateId, input)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to trigger test build')
        return null
      } finally {
        setBuilding(false)
      }
    },
    []
  )

  return { building, error, triggerBuild }
}
